// src/hooks/useTheme.tsx

import React, {
  useState,
  useEffect,
  createContext,
  useContext,
  ReactNode,
} from "react";
import { getSettings } from "../services/database";
import { logger } from "../utils/logger";

export type Theme = "dark" | "light";

export interface ThemeContextType {
  theme: Theme;
  toggleTheme: () => void;
  setTheme: (theme: Theme) => void;
}

const ThemeContext = createContext<ThemeContextType | null>(null);

export const useTheme = (): ThemeContextType => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error("useTheme must be used within a ThemeProvider");
  }
  return context;
};

interface ThemeProviderProps {
  children: ReactNode;
  defaultTheme?: Theme;
}

const isValidTheme = (theme: string): theme is Theme => {
  return theme === "dark" || theme === "light";
};

const getStoredTheme = (): Theme | null => {
  try {
    const stored = localStorage.getItem("portfolio-theme");
    return stored && isValidTheme(stored) ? stored : null;
  } catch {
    return null;
  }
};

export const ThemeProvider: React.FC<ThemeProviderProps> = ({
  children,
  defaultTheme = "dark",
}) => {
  const [theme, setThemeState] = useState<Theme>(defaultTheme);
  const [isInitialized, setIsInitialized] = useState(false);

  // FIXED: Proper initialization order
  useEffect(() => {
    const initializeTheme = async () => {
      try {
        // Priority 1: Check localStorage for user's manual preference
        const storedTheme = getStoredTheme();
        if (storedTheme) {
          logger.info("Using user's saved theme preference:", storedTheme);
          setThemeState(storedTheme);
          setIsInitialized(true);
          return;
        }

        // Priority 2: Fetch admin's default theme setting
        logger.info("Fetching default theme from admin settings");
        const settings = await getSettings();

        if (settings?.theme) {
          logger.info("Using admin's default theme:", settings.theme);
          setThemeState(settings.theme);
        } else {
          logger.info("No admin theme found, using fallback:", defaultTheme);
          setThemeState(defaultTheme);
        }

        setIsInitialized(true);
      } catch (error) {
        logger.error("Error initializing theme:", error);
        setThemeState(defaultTheme);
        setIsInitialized(true);
      }
    };

    initializeTheme();
  }, []); // Run only once on mount

  // Apply theme to document whenever it changes
  useEffect(() => {
    if (!isInitialized) return;

    logger.info("Applying theme to document:", theme);

    // Apply to html element
    document.documentElement.setAttribute("data-theme", theme);

    // Also apply as class for additional styling support
    document.documentElement.classList.remove("dark", "light");
    document.documentElement.classList.add(theme);

    // Save user's preference to localStorage
    try {
      localStorage.setItem("portfolio-theme", theme);
    } catch (error) {
      logger.warn("Failed to save theme to localStorage:", error);
    }
  }, [theme, isInitialized]);

  const toggleTheme = () => {
    setThemeState((prev) => {
      const newTheme = prev === "dark" ? "light" : "dark";
      logger.info("Toggling theme:", prev, "→", newTheme);
      return newTheme;
    });
  };

  const setTheme = (newTheme: Theme) => {
    setThemeState(newTheme);
  };

  const value: ThemeContextType = {
    theme,
    toggleTheme,
    setTheme,
  };

  // Show minimal loading UI until theme is initialized
  if (!isInitialized) {
    return (
      <div style={{
        width: '100vw',
        height: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: defaultTheme === 'dark' ? '#0a0a0a' : '#ffffff'
      }}>
        <div className="loading-spinner"></div>
      </div>
    );
  }

  return (
    <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>
  );
};